import React, { useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import AdminPanel from "../shared/AdminPanel";
import panelStyles from "../styles/Panel.module.css";
import styles from "../styles/LogsSection.module.css";
import { buildLast7DaysLogSeries } from "../utils/chartUtils";

function LogsSectionBody({ state, actions }) {
  const {
    logId,
    logsUserId,
    dbQuery,
    log,
    userLogs,
    chartData,
    lastLogs,
    filteredLastLogs,
    runtimeLevel,
    runtimeQuery,
    runtimeLogs,
    runtimeLimit,
    runtimeAuto,
    runtimePaused
  } = state;

  const logsSeries = buildLast7DaysLogSeries(chartData);
  const [selectedLog, setSelectedLog] = useState(null);
  const lastLogsRef = useRef(null);
  const runtimeLogsRef = useRef(null);

  const runtimeFiltered = useMemo(() => {
    return (Array.isArray(runtimeLogs) ? runtimeLogs : []).filter((row) => {
      if (!runtimeQuery) return true;
      const hay = `${row?.message || ""} ${row?.file || ""}:${row?.line || ""}`.toLowerCase();
      return hay.includes(String(runtimeQuery).toLowerCase());
    });
  }, [runtimeLogs, runtimeQuery]);

  const rowsLastLogs = Array.isArray(filteredLastLogs)
    ? filteredLastLogs
    : Array.isArray(lastLogs)
    ? lastLogs
    : [];

  const getRuntimeRowClass = (level) => {
    const lv = String(level || "").toLowerCase();
    if (lv === "error") return `${styles.runtimeItem} ${styles.levelError}`;
    if (lv === "warn" || lv === "warning") return `${styles.runtimeItem} ${styles.levelWarn}`;
    if (lv === "info") return `${styles.runtimeItem} ${styles.levelInfo}`;
    if (lv === "log") return `${styles.runtimeItem} ${styles.levelLog}`;
    return `${styles.runtimeItem} ${styles.levelOther}`;
  };

  const toggleAllDetails = (root, open) => {
    const el = root?.current;
    if (!el) return;
    el.querySelectorAll("details").forEach((node) => {
      node.open = Boolean(open);
    });
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
    } catch (_) {}
  };

  return (
    <div className={styles.grid}>
      <div className={styles.block}>
        <h3>Лог по ID</h3>
        <div className={styles.controls}>
          <input
            type="text"
            value={logId}
            className={panelStyles.input}
            placeholder="ID лога"
            onChange={(event) => actions.setLogId(event.target.value)}
          />
          <button type="button" className={panelStyles.button} onClick={actions.searchLogById}>
            Найти
          </button>
        </div>
        {log ? (
          <div className={styles.card}>
            <pre className={styles.pre}>{`ID: ${log.id}
User ID: ${log.user_id}
Вопрос: ${log.user_question}
Ответ GPT: ${log.gpt_answer}
Создан: ${new Date(log.created_at).toLocaleString()}`}</pre>
            <div className={styles.rowButtons}>
              <button type="button" className={panelStyles.button} onClick={() => copyText(log.user_question)}>
                Copy вопрос
              </button>
              <button type="button" className={panelStyles.button} onClick={() => copyText(log.gpt_answer)}>
                Copy ответ
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.block}>
        <h3>Логи пользователя</h3>
        <div className={styles.controls}>
          <input
            type="text"
            value={logsUserId}
            className={panelStyles.input}
            placeholder="user_id"
            onChange={(event) => actions.setLogsUserId(event.target.value)}
          />
          <button type="button" className={panelStyles.button} onClick={actions.searchLogsByUser}>
            Загрузить
          </button>
        </div>
        {userLogs.length > 0 ? (
          <div className={panelStyles.tableWrap}>
            <table className={panelStyles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Вопрос</th>
                  <th>Ответ</th>
                  <th>Создан</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {userLogs.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.id}</td>
                    <td>{entry.user_id}</td>
                    <td className={styles.truncate}>{entry.user_question}</td>
                    <td className={styles.truncate}>{entry.gpt_answer}</td>
                    <td>{new Date(entry.created_at).toLocaleString()}</td>
                    <td>
                      <button type="button" className={panelStyles.button} onClick={() => setSelectedLog(entry)}>
                        Открыть
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={panelStyles.note}>Логи пользователя ещё не загружены.</div>
        )}
      </div>

      <div className={styles.block}>
        <h3>Сообщения за последние 7 дней</h3>
        <div className={styles.chart}>
          <Line
            data={{
              labels: logsSeries.labels,
              datasets: [
                {
                  label: "Сообщения",
                  data: logsSeries.data,
                  borderColor: "rgba(124, 231, 255, 1)",
                  backgroundColor: "rgba(124, 231, 255, 0.18)"
                }
              ]
            }}
            options={{ responsive: true }}
          />
        </div>
      </div>

      <div className={styles.block}>
        <h3>Последние 100 логов</h3>
        <div className={styles.controls}>
          <input
            type="text"
            value={dbQuery}
            className={panelStyles.input}
            placeholder="Поиск по id/user/тексту"
            onChange={(event) => actions.setDbQuery(event.target.value)}
          />
          <button type="button" className={panelStyles.button} onClick={actions.reloadLastLogs}>
            Обновить
          </button>
          <button type="button" className={panelStyles.buttonSecondary} onClick={() => toggleAllDetails(lastLogsRef, true)}>
            Развернуть все
          </button>
          <button type="button" className={panelStyles.buttonSecondary} onClick={() => toggleAllDetails(lastLogsRef, false)}>
            Свернуть все
          </button>
          <div className={styles.meta}>Найдено: {rowsLastLogs.length}</div>
        </div>
        {rowsLastLogs.length > 0 ? (
          <div className={styles.logList} ref={lastLogsRef}>
            {rowsLastLogs.map((entry) => {
              const created = entry?.created_at ? new Date(entry.created_at).toLocaleString() : "—";
              const preview = String(entry?.user_question || entry?.gpt_answer || "").trim();
              return (
                <details key={entry.id} className={styles.logItem}>
                  <summary className={styles.logSummary}>
                    <span className={styles.logMeta}>
                      <span className={styles.logId}>#{entry.id}</span>
                      <span className={styles.logUser}>u{entry.user_id}</span>
                      <span className={styles.logTime}>{created}</span>
                    </span>
                    <span className={styles.logPreview} title={preview}>
                      {preview || "—"}
                    </span>
                  </summary>
                  <div className={styles.logBody}>
                    <div className={styles.logCols}>
                      <div>
                        <div className={styles.logLabel}>Вопрос</div>
                        <pre className={styles.pre}>{entry.user_question || ""}</pre>
                        <div className={styles.rowButtons}>
                          <button type="button" className={panelStyles.button} onClick={() => copyText(entry.user_question)}>
                            Copy вопрос
                          </button>
                          <button type="button" className={panelStyles.buttonSecondary} onClick={() => setSelectedLog(entry)}>
                            Открыть в окне
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className={styles.logLabel}>Ответ</div>
                        <pre className={styles.pre}>{entry.gpt_answer || ""}</pre>
                        <div className={styles.rowButtons}>
                          <button type="button" className={panelStyles.button} onClick={() => copyText(entry.gpt_answer)}>
                            Copy ответ
                          </button>
                          <button type="button" className={panelStyles.buttonSecondary} onClick={() => setSelectedLog(entry)}>
                            Открыть в окне
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        ) : (
          <div className={panelStyles.note}>Логи отсутствуют.</div>
        )}
      </div>

      <div className={styles.block}>
        <h3>Runtime логи (console.log/error) — последние 24ч</h3>
        <div className={styles.controls}>
          <select className={panelStyles.select} value={runtimeLevel} onChange={(e) => actions.setRuntimeLevel(e.target.value)}>
            <option value="">Все уровни</option>
            <option value="error">error</option>
            <option value="warn">warn</option>
            <option value="info">info</option>
            <option value="log">log</option>
          </select>
          <select className={panelStyles.select} value={runtimeLimit} onChange={(e) => actions.setRuntimeLimit(Number(e.target.value) || 300)}>
            <option value={100}>100</option>
            <option value={300}>300</option>
            <option value={600}>600</option>
            <option value={1000}>1000</option>
          </select>
          <input
            type="text"
            value={runtimeQuery}
            className={panelStyles.input}
            placeholder="Поиск по тексту/файлу"
            onChange={(event) => actions.setRuntimeQuery(event.target.value)}
          />
          <button type="button" className={panelStyles.button} onClick={actions.applyRuntimeFilter}>
            Обновить
          </button>
          <button type="button" className={panelStyles.button} onClick={actions.pollRuntimeNow}>
            + Новые
          </button>
          <button type="button" className={panelStyles.buttonSecondary} onClick={() => toggleAllDetails(runtimeLogsRef, true)}>
            Развернуть все
          </button>
          <button type="button" className={panelStyles.buttonSecondary} onClick={() => toggleAllDetails(runtimeLogsRef, false)}>
            Свернуть все
          </button>
          <label className={styles.toggle}>
            <input type="checkbox" checked={runtimeAuto} onChange={(e) => actions.setRuntimeAuto(e.target.checked)} />
            Авто
          </label>
          <label className={styles.toggle}>
            <input type="checkbox" checked={runtimePaused} onChange={(e) => actions.setRuntimePaused(e.target.checked)} />
            Пауза
          </label>
          <div className={styles.meta}>Строк: {runtimeFiltered.length}</div>
        </div>

        {runtimeFiltered.length > 0 ? (
          <div className={styles.logList} ref={runtimeLogsRef}>
            {runtimeFiltered.map((row, index) => {
              const ts = row?.ts ? new Date(row.ts).toLocaleString() : "—";
              const file = row?.file ? `${row.file}:${row.line || ""}` : "—";
              const msg = String(row?.message || "");
              return (
                <details key={`rt-${row.ts || 0}-${index}`} className={getRuntimeRowClass(row.level)}>
                  <summary className={styles.runtimeSummary}>
                    <span className={styles.runtimeMeta}>
                      <span className={styles.runtimeTs}>{ts}</span>
                      <span className={styles.runtimeLevel}>{String(row.level || "other")}</span>
                      <span className={styles.runtimeFile} title={file}>{file}</span>
                    </span>
                    <span className={styles.runtimePreview} title={msg}>{msg || "—"}</span>
                  </summary>
                  <div className={styles.runtimeBody}>
                    <pre className={styles.runtimeMessage}>{msg}</pre>
                    <div className={styles.rowButtons}>
                      <button type="button" className={panelStyles.button} onClick={() => copyText(msg)}>
                        Copy
                      </button>
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        ) : (
          <div className={panelStyles.note}>Нет runtime логов (или фильтр не совпал).</div>
        )}
      </div>

      {selectedLog ? (
        <div className={styles.modalOverlay} role="presentation" onClick={() => setSelectedLog(null)}>
          <div className={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>Log #{selectedLog.id}</div>
              <button type="button" className={panelStyles.button} onClick={() => setSelectedLog(null)}>
                Закрыть
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalMeta}>
                <div><b>User:</b> {selectedLog.user_id}</div>
                <div><b>Created:</b> {new Date(selectedLog.created_at).toLocaleString()}</div>
              </div>
              <div className={styles.modalCols}>
                <div>
                  <div className={styles.modalLabel}>Вопрос</div>
                  <pre className={styles.pre}>{selectedLog.user_question}</pre>
                  <button type="button" className={panelStyles.button} onClick={() => copyText(selectedLog.user_question)}>
                    Copy вопрос
                  </button>
                </div>
                <div>
                  <div className={styles.modalLabel}>Ответ</div>
                  <pre className={styles.pre}>{selectedLog.gpt_answer}</pre>
                  <button type="button" className={panelStyles.button} onClick={() => copyText(selectedLog.gpt_answer)}>
                    Copy ответ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LogsSection({ mode = "panel", isOpen = true, onToggle, state, actions }) {
  if (mode === "page") {
    return <LogsSectionBody state={state} actions={actions} />;
  }

  return (
    <AdminPanel id="logs" title="Логи" isOpen={isOpen} onToggle={onToggle}>
      <LogsSectionBody state={state} actions={actions} />
    </AdminPanel>
  );
}

export default LogsSection;
